import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Spin, Tabs, Button, message } from 'antd';
import { Desktop } from 'assets/preview/Desktop';
import { Mobile } from 'assets/preview/Mobile';
import { useNavigate } from 'react-router-dom';
import IntroManagement, { IntroComponentRef } from '../components/Intro';
import QuestionManagement, {
    IQuestionManagement,
} from '../components/Questions';
import PreviewIntro from '../components/PreviewIntro';
import PreviewQuestion from '../components/PreviewQuestion';

import { PATH } from 'constants/path';
import s from './s.module.less';
import commonTabS from 'styles/common.tab.less';
import {
    getAssessmentList,
    addOrUpdateAssessment,
} from 'api/operation/assessment';
import dayjs from 'dayjs';
import { getUrlParam } from 'utils/common';
import { EFeatureType } from 'constants/textVisit';
import WarningTips from 'components/WarningTips';

import type {
    TAssessmentItem,
    TPreviewQuestion,
} from 'types/operation';

const Detail: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isMobileMode, setIsMobileMode] = useState(false);
    const [tabActiveKey, setTabActiveKey] = useState('1');
    const [isShowWarningTips, setIsShowWarningTips] = useState(true);

    const navigate = useNavigate();
    const id = getUrlParam('id');
    const uuid = getUrlParam('uuid');
    const [info, setInfo] = useState<TAssessmentItem>({} as TAssessmentItem);

    const [updateTime, setUpdateTime] = useState<string>('');
    const [goLiveObj, setGoLiveObj] = useState<TAssessmentItem>(
        {} as TAssessmentItem,
    );

    const introRef = useRef<IntroComponentRef>(null);
    const questionRef = useRef<IQuestionManagement>(null);
    const [previewQuestion, setPreviewQuestion] = useState<TPreviewQuestion>(
        {} as TPreviewQuestion,
    );

    const getInfo = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data } = await getAssessmentList({
                assessmentName: '',
                tags: [''],
                id,
                featureType: EFeatureType.TEXT_VISIT_WORKFLOW,
                orderKey: 'UPDATE_AT',
                orderType: 'DESC',
                size: 1,
                current: 1,
            });
            if (data?.data?.records?.length > 0) {
                const result = data.data.records[0];
                setInfo(result);
                const lastUpdateTime = `${dayjs(result.updateAt).format(
                    'MMMM D, YYYY',
                )} at ${dayjs(result.updateAt).format('h:mm A')}`;
                setUpdateTime(lastUpdateTime);
                setGoLiveObj(result);
            } else {
                console.log('TextVisitEmbeddableWorkflows: No records found in API response');
                console.log('TextVisitEmbeddableWorkflows API response: ', data);
            }
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
        }
    }, [id]);

    const handleTabClick = useCallback((key: string) => {
        setTabActiveKey(key);
    }, []);

    const handleExit = useCallback(() => {
        navigate(`${PATH.OPERATION}/${PATH.TEXT_VISIT_EMBEDDABLE_WORKFLOWS}`, {
            replace: true,
        });
    }, [navigate]);

    const handleAction = async (obj: Partial<TAssessmentItem>) => {
        setGoLiveObj({
            ...goLiveObj,
            ...obj,
        });
        const { data } = await addOrUpdateAssessment({
            ...goLiveObj,
            ...obj,
            uuid,
            id,
            fromType: 'google-form',
        });
        if (data.code === 200) {
            message.success('Information updated successfully');
        }
    };

    const handlePreviewQuestion = (item: TPreviewQuestion) => {
        setPreviewQuestion(item);
    };

    useEffect(() => {
        getInfo();
    }, [getInfo]);

    const homeTabItems = [
        {
            key: '1',
            label: <span className={s.tabLabel}>Intro</span>,
            children: (
                <IntroManagement
                    isLoading={isLoading}
                    goLiveObj={goLiveObj}
                    onSuccess={(item) => handleAction(item)}
                    ref={introRef}
                />
            ),
        },
        {
            key: '2',
            label: <span className={s.tabLabel}>Questions</span>,
            children: (
                <QuestionManagement
                    goLiveObj={goLiveObj}
                    onSuccess={() => getInfo()}
                    onPreview={(item: TPreviewQuestion) => handlePreviewQuestion(item)}
                    ref={questionRef}
                />
            ),
        },
    ];

    const renderHeader = (
        <div className={s.titleWrap}>
            <h2 className={s.title}>{info.assessmentName}</h2>
            <h2 className={s.time}>Date last modified: {updateTime}</h2>
        </div>
    );

    const renderPreview = (
        <div className={s.preview}>
            <div className={s.modeWrap}>
                <span
                    onClick={() => {
                        setIsMobileMode(false);
                    }}
                    className={isMobileMode ? '' : s.active}
                >
                    <Desktop />
                </span>
                <span
                    onClick={() => {
                        setIsMobileMode(true);
                    }}
                    className={isMobileMode ? s.active : ''}
                >
                    <Mobile />
                </span>
            </div>
            <div className={s.previewContent}>
                {tabActiveKey === '1' && (
                    <PreviewIntro isMobileMode={isMobileMode} previewItem={goLiveObj} />
                )}
                {tabActiveKey === '2' && (
                    <PreviewQuestion
                        isMobileMode={isMobileMode}
                        previewItem={previewQuestion}
                    />
                )}
                {tabActiveKey === '3' && (
                    <div className={s.decisionPreview}>
                        <div className={s.noDecisionPreview}>
                            <h3>Decision Tree Configuration</h3>
                            <p>Configure decision rules for this assessment. Rules will determine user flow based on their answers.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderFooter = (
        <div className={s.footer}>
            <Button type="primary" ghost onClick={handleExit}>
                Exit
            </Button>
            {/* <Button type="primary" onClick={handleGoLive}>
                Go live
            </Button> */}
        </div>
    );

    return (
        <div className={s.wrap}>
            <Spin spinning={isLoading}>
                <div className={s.content}>
                    <div className={s.setting}>
                        {renderHeader}
                        <div className={s.warningTips}>
                            <WarningTips
                                title="Workflow activation rules"
                                description="Please note that after making any changes to this workflow, you must return to the workflow overview page and click Activate again. If not re-activated, the live link will become inactive."
                                closable
                                isShow={isShowWarningTips}
                                onClose={() => setIsShowWarningTips(false)}
                            />
                        </div>
                        <div className={commonTabS.content}>
                            <Tabs
                                defaultActiveKey="1"
                                activeKey={tabActiveKey}
                                items={homeTabItems}
                                onTabClick={handleTabClick}
                            />
                        </div>
                    </div>
                    {renderPreview}
                </div>
                {renderFooter}
            </Spin>
        </div>
    );
};

export default Detail;
